const DB_NAME = "brian-recordings";
const STORE = "pending";
const CHUNK_STORE = "chunks";
const META_STORE = "recordingMeta";
const VERSION = 3;

export interface PendingRecording {
  id: string;
  about: string[];
  blob: Blob;
  createdAt: number;
  attempts: number;
  artifactUuid?: string;
  transcriptUuid?: string;
  failed?: boolean;
}

export interface RecordingMeta {
  recordingId: string;
  about: string[];
  createdAt: number;
  transcriptUuid?: string;
}

interface ChunkRow {
  recordingId: string;
  seq: number;
  data: ArrayBuffer;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE))
        db.createObjectStore(STORE, { keyPath: "id" });
      if (!db.objectStoreNames.contains(CHUNK_STORE))
        db.createObjectStore(CHUNK_STORE, { keyPath: ["recordingId", "seq"] });
      if (!db.objectStoreNames.contains(META_STORE))
        db.createObjectStore(META_STORE, { keyPath: "recordingId" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  try {
    return await promisify(fn(db.transaction(store, mode).objectStore(store)));
  } finally {
    db.close();
  }
}

export const savePendingRecording = (rec: PendingRecording): Promise<unknown> =>
  withStore(STORE, "readwrite", (s) => s.put(rec));

export const deletePendingRecording = (id: string): Promise<unknown> =>
  withStore(STORE, "readwrite", (s) => s.delete(id));

export const getPendingRecording = (
  id: string,
): Promise<PendingRecording | undefined> =>
  withStore(
    STORE,
    "readonly",
    (s) => s.get(id) as IDBRequest<PendingRecording | undefined>,
  );

export const listPendingRecordings = (): Promise<PendingRecording[]> =>
  withStore(
    STORE,
    "readonly",
    (s) => s.getAll() as IDBRequest<PendingRecording[]>,
  );

const chunkRange = (recordingId: string) =>
  IDBKeyRange.bound([recordingId], [recordingId, []]);

export const appendChunk = (
  recordingId: string,
  seq: number,
  data: ArrayBuffer,
): Promise<unknown> =>
  withStore(CHUNK_STORE, "readwrite", (s) =>
    s.put({ recordingId, seq, data } satisfies ChunkRow),
  );

export const readChunks = async (
  recordingId: string,
): Promise<ArrayBuffer[]> => {
  const rows = await withStore(CHUNK_STORE, "readonly", (s) =>
    s.getAll(chunkRange(recordingId)),
  );
  return (rows as ChunkRow[]).map((r) => r.data);
};

export const clearChunks = (recordingId: string): Promise<unknown> =>
  withStore(CHUNK_STORE, "readwrite", (s) => s.delete(chunkRange(recordingId)));

export const saveRecordingMeta = (meta: RecordingMeta): Promise<unknown> =>
  withStore(META_STORE, "readwrite", (s) => s.put(meta));

export const deleteRecordingMeta = (recordingId: string): Promise<unknown> =>
  withStore(META_STORE, "readwrite", (s) => s.delete(recordingId));

export const getRecordingMeta = (
  recordingId: string,
): Promise<RecordingMeta | undefined> =>
  withStore(
    META_STORE,
    "readonly",
    (s) => s.get(recordingId) as IDBRequest<RecordingMeta | undefined>,
  );

export const listRecordingMeta = (): Promise<RecordingMeta[]> =>
  withStore(
    META_STORE,
    "readonly",
    (s) => s.getAll() as IDBRequest<RecordingMeta[]>,
  );
